import { ReactElement } from 'react';
import Select, { MenuPlacement, ValueType } from 'react-select';
import { Controller, Control, FieldErrors } from 'react-hook-form';
import get from 'lodash/get';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import FormHelperText from '@material-ui/core/FormHelperText';

import { IOption } from './types';
import customStyles from './customStyles';
import styles from './Select.module.scss';

type IValue = string | number;

type IsMulti = boolean;

interface IProps {
  control: Control<any>;
  errors: FieldErrors;
  name: string;
  options: IOption[];
  label?: string;
  required?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  isSearchable?: boolean;
  maxMenuHeight?: number;
  isMulti?: boolean;
  placeholder?: string;
  isInBody?: boolean;
  menuPlacement?: MenuPlacement;
}

const SelectComponent = ({
  control,
  errors,
  name,
  options,
  label,
  required = false,
  isDisabled = false,
  isLoading = false,
  isMulti = false,
  isSearchable = true,
  maxMenuHeight = 180,
  placeholder = '',
  isInBody = false,
  menuPlacement = 'bottom',
}: IProps): ReactElement => {
  const error = get(errors, `${name}.message`, '');

  const formatValue = (option: ValueType<IOption | IOption[], IsMulti>) => {
    const value = isMulti
      ? (option as IOption[]).map((item: IOption) => item.value)
      : (option as IOption)?.value ?? '';
    return value;
  };

  const getValue = (value: IValue | IValue[]): IOption | IOption[] | null => {
    if (!options) return isMulti ? [] : null;

    if (isMulti) {
      if (!Array.isArray(value) || value.length === 0) return [];
      return options.filter(o => value.includes(o.value));
    }

    return options.find(o => o.value === value) ?? null;
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange } }) => (
        <FormControl required={required} component="fieldset" className={styles.selectControl}>
          <FormLabel component="legend" className={styles.label}>
            {label}
          </FormLabel>
          <Select
            instanceId={name}
            styles={customStyles}
            isDisabled={isDisabled}
            isLoading={isLoading}
            isSearchable={isSearchable}
            maxMenuHeight={maxMenuHeight}
            name={name}
            options={options}
            placeholder={placeholder}
            isMulti={isMulti}
            menuPortalTarget={isInBody ? document.body : null}
            value={getValue(value)}
            onChange={val => onChange(formatValue(val))}
            menuPlacement={menuPlacement}
          />
          {error && <FormHelperText error>{error}</FormHelperText>}
        </FormControl>
      )}
    />
  );
};

export default SelectComponent;
